import React, { useState, useEffect } from 'react';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import VideocamIcon from "@mui/icons-material/Videocam";
import PhoneIcon from "@mui/icons-material/Phone";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {
  Table as MuiTable, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Box, Typography, CircularProgress, Alert, Chip, TextField,
  InputAdornment, IconButton,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAllAppointments } from '../../service/appointment';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../navbar';


const AppointmentsResult = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  const { startDate: navStartDate, endDate: navEndDate } = location.state || {};

  const fetchAppointments = async (start_date, end_date) => {
    setLoading(true); setError('');
    try {
      const userID = user?.id || '';
      const response = await getAllAppointments({ start_date, end_date, user_id: userID });
      setAppointments(response || []);
    } catch (err) {
      setError('Failed to fetch appointments. Please try again.');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (navStartDate && navEndDate) {
      setStartDate(navStartDate); setEndDate(navEndDate);
      fetchAppointments(navStartDate, navEndDate);
    }
  }, [navStartDate, navEndDate]);

  useEffect(() => {
    setCurrentPage(1);
    if (search.trim()) {
      const q = search.toLowerCase();
      setFilteredAppointments(appointments.filter(a =>
        a.user?.toLowerCase().includes(q) || a.booked_for?.toLowerCase().includes(q) ||
        a.account?.toLowerCase().includes(q) || a.doctor_name?.toLowerCase().includes(q) ||
        a.status?.toLowerCase().includes(q) || a.consultation_type?.toLowerCase().includes(q)
      ));
    } else { setFilteredAppointments(appointments); }
  }, [search, appointments]);

  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const formatTime = (t) => {
    if (!t) return 'N/A';
    const [h, m] = t.split(':');
    const dt = new Date(); dt.setHours(+h, +m);
    return dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };
  const formatDateTime = (s) => {
    if (!s) return 'N/A';
    return new Date(s).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'UTC' });
  };

  const statusStyle = (s) => {
    switch (s?.toLowerCase()) {
      case 'scheduled': return { bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' };
      case 'completed': return { bgcolor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' };
      case 'cancelled': case 'canceled': return { bgcolor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' };
      case 'not_attended': return { bgcolor: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' };
      default: return { bgcolor: '#f9fafb', color: '#6b7280', border: '1px solid #e5e7eb' };
    }
  };

  const getPlatformName = (bf) => {
    if (bf === 'echobuddy' || bf === 'echocare') return 'diginxthlt';
    if (bf === 'musicccare') return 'apollo';
    return bf || 'N/A';
  };

  const downloadCSV = () => {
    const headers = ['Username', 'App Name', 'Account', 'Date', 'Time', 'Counsellor', 'Status', 'Consultation Type', 'Booking DateTime'];
    const rows = filteredAppointments.map(a => [
      a.user || 'N/A', a.app_name || 'N/A', a.account || 'N/A',
      formatDate(a.slot_date), `${formatTime(a.start_time)} to ${formatTime(a.end_time)}`,
      a.doctor_name || 'N/A', a.status?.replace(/_/g, ' ').toUpperCase() || 'N/A',
      a.consultation_type || 'N/A', formatDateTime(a.created_at)
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    link.setAttribute('download', `appointments_${startDate}_to_${endDate}.csv`);
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAppointments = filteredAppointments.slice(startIndex, startIndex + itemsPerPage);

  return (
    <>
      <Navbar />
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)', px: { xs: 2, sm: 4 }, pb: 5, pt: 3 }}>

        {/* ── Header ── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#111827' }}>Appointment Records</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', mt: 0.3 }}>
              {startDate && endDate ? `${formatDate(startDate)} — ${formatDate(endDate)}` : 'Review and track appointments'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField
              size="small" placeholder="Search appointments..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: '1rem', color: '#9ca3af' }} /></InputAdornment> }}
              sx={{ width: { xs: 200, sm: 280 }, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', fontSize: '0.85rem' } }}
            />
            <IconButton
              onClick={downloadCSV}
              sx={{ border: '1px solid #22c55e', borderRadius: 2, color: '#22c55e', p: 0.75, '&:hover': { bgcolor: '#22c55e', color: '#fff' } }}
            >
              <DownloadIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
            <Button
              variant="outlined" size="small" startIcon={<ArrowBackIcon sx={{ fontSize: '0.9rem' }} />}
              onClick={() => navigate('/all-appointments')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, fontSize: '0.82rem', borderColor: '#e2e8f0', color: '#6b7280', '&:hover': { borderColor: '#3b82f6', color: '#3b82f6', bgcolor: '#eff6ff' } }}
            >
              Back
            </Button>
          </Box>
        </Box>

        {/* ── Stats strip ── */}
        {!loading && filteredAppointments.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
            {[
              { label: 'Total', count: filteredAppointments.length, bg: '#eff6ff', color: '#1d4ed8' },
              { label: 'Completed', count: filteredAppointments.filter(a => a.status?.toLowerCase() === 'completed').length, bg: '#f0fdf4', color: '#15803d' },
              { label: 'Scheduled', count: filteredAppointments.filter(a => a.status?.toLowerCase() === 'scheduled').length, bg: '#fffbeb', color: '#b45309' },
              { label: 'Cancelled', count: filteredAppointments.filter(a => ['cancelled','canceled'].includes(a.status?.toLowerCase())).length, bg: '#fef2f2', color: '#b91c1c' },
            ].map(s => (
              <Box key={s.label} sx={{ bgcolor: s.bg, border: `1px solid ${s.color}22`, borderRadius: 2, px: 2, py: 1, display: 'flex', flexDirection: 'column', minWidth: 90 }}>
                <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.count}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: s.color, fontWeight: 600, mt: 0.2 }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={36} sx={{ color: '#3b82f6' }} />
          </Box>
        )}

        {!loading && !error && (
          <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
            <TableContainer>
              <MuiTable size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {['Patient', 'Date & Time', 'Counsellor', 'Status', 'Mode', 'Booked On'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5, borderBottom: '2px solid #e2e8f0' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAppointments.length > 0 ? paginatedAppointments.map((a, i) => (
                    <TableRow key={a.id || i} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '& td': { py: 1.5, borderBottom: '1px solid #f1f5f9' } }}>

                      {/* Patient */}
                      <TableCell>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#111827' }}>{a.user || 'N/A'}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          {a.account && (
                            <Chip label={a.account} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569', '& .MuiChip-label': { px: 0.75 } }} />
                          )}
                          {a.app_name && (
                            <Chip label={a.app_name} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#eff6ff', color: '#1d4ed8', '& .MuiChip-label': { px: 0.75 } }} />
                          )}
                        </Box>
                      </TableCell>

                      {/* Date & Time */}
                      <TableCell>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#111827' }}>{formatDate(a.slot_date)}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mt: 0.2 }}>{formatTime(a.start_time)} – {formatTime(a.end_time)}</Typography>
                      </TableCell>

                      {/* Counsellor */}
                      <TableCell>
                        <Typography sx={{ fontWeight: 500, fontSize: '0.85rem', color: '#374151' }}>{a.doctor_name || 'N/A'}</Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip label={a.status?.replace(/_/g, ' ') || 'N/A'} size="small"
                          sx={{ ...statusStyle(a.status), fontWeight: 700, fontSize: '0.72rem', height: 22, textTransform: 'capitalize', '& .MuiChip-label': { px: 1 } }} />
                      </TableCell>

                      {/* Mode icon */}
                      <TableCell>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4,
                          borderRadius: 1.5, fontSize: '0.75rem', fontWeight: 600,
                          bgcolor: a.consultation_type?.toLowerCase() === 'video' ? '#eff6ff' : a.consultation_type?.toLowerCase() === 'audio' ? '#fff7ed' : '#f0fdf4',
                          color: a.consultation_type?.toLowerCase() === 'video' ? '#1d4ed8' : a.consultation_type?.toLowerCase() === 'audio' ? '#c2410c' : '#15803d',
                        }}>
                          {a.consultation_type?.toLowerCase() === 'video' && <VideocamIcon sx={{ fontSize: '0.9rem' }} />}
                          {a.consultation_type?.toLowerCase() === 'audio' && <PhoneIcon sx={{ fontSize: '0.9rem' }} />}
                          {a.consultation_type?.toLowerCase() === 'chat' && <ChatBubbleOutlineIcon sx={{ fontSize: '0.9rem' }} />}
                          <span>{a.consultation_type || 'N/A'}</span>
                        </Box>
                      </TableCell>

                      {/* Booked On */}
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>{formatDateTime(a.created_at)}</Typography>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}>
                        <Typography sx={{ color: '#9ca3af', fontSize: '0.9rem' }}>No appointments found for this date range</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </MuiTable>
            </TableContainer>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2, borderTop: '1px solid #f1f5f9' }}>
                <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>
                  Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredAppointments.length)} of {filteredAppointments.length}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}
                    sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.8rem' }} variant="outlined">Previous</Button>
                  <Button size="small" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}
                    sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.8rem' }} variant="outlined">Next</Button>
                </Box>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </>
  );
};

export default AppointmentsResult;
